import axios, { type AxiosError } from 'axios'
import { useQuery } from 'context/QueryProvider'
import { useMemo } from 'react'
import { queryStringSeparator } from 'services/url'
import type { PagedCollection } from 'shared/types'
import useSWR, { type KeyedMutator, type SWRConfiguration, useSWRConfig } from 'swr'
import useSWRInfinite from 'swr/infinite'

type SWRCacheData<T> = T extends Array<infer Item> ? T | PagedCollection<Item> : T

export interface SWROutput<T> {
  data: T | undefined
  error: AxiosError<unknown> | undefined
  isValidating: boolean
  mutate: KeyedMutator<SWRCacheData<T>>
  total: number | undefined
}

interface HydraCollectionPage<TItem> {
  'hydra:member'?: TItem[]
  'hydra:totalItems'?: number
  'hydra:view'?: {
    'hydra:last'?: string
  }
  nbResults?: number | null
}

type InfiniteScrollPage<TItem> = HydraCollectionPage<TItem> | TItem[]

interface SwrOptions<TPage> {
  onSuccess?: (data: TPage[]) => void
  onError?: (error: unknown) => void
}

export default function useSWRCustom<T = any>(url: string | null, options?: SWRConfiguration): SWROutput<T> {
  const { client } = useQuery()
  const { fetcher: customFetcher, ...swrOptions } = options ?? {}

  const fetcher = async (URL: string) => {
    const isPublicRoute = URL.startsWith('/api/public/')
    const headers = isPublicRoute ? {} : client.headers
    const resp = await axios.get(URL, { headers })
    return resp?.data
  }

  const { data, error, isValidating, mutate } = useSWR<T>(url, customFetcher ?? fetcher, swrOptions)

  return {
    data: ((data as PagedCollection<T>)?.['hydra:member'] ?? data) as T | undefined,
    error,
    isValidating,
    mutate,
    total: (data as PagedCollection<T>)?.['hydra:totalItems'] ?? (data as any)?.nbResults ?? undefined,
  } as SWROutput<T>
}

export function useSWRInfiniteScroll<TItem>(url: string | null, options?: SwrOptions<InfiniteScrollPage<TItem>>) {
  const { client } = useQuery()

  const fetcher = async (URL: string): Promise<InfiniteScrollPage<TItem>> => {
    const isPublicRoute = URL.startsWith('/api/public/')
    const headers = isPublicRoute ? {} : client.headers
    const resp = await axios.get(URL, { headers })
    return resp?.data
  }

  const { data, mutate, size, setSize, isValidating } = useSWRInfinite<InfiniteScrollPage<TItem>>(
    index => {
      if (!url) return null
      return `${url}${queryStringSeparator(String(url))}page=${index + 1}`
    },
    fetcher,
    options,
  )

  const firstPage = data?.[0]
  const lastPageURL = firstPage && !Array.isArray(firstPage) ? firstPage['hydra:view']?.['hydra:last'] : null
  const lastPage = lastPageURL ? new URLSearchParams(lastPageURL.split('?')[1]).get('page') : null
  const items = useMemo(
    () => data?.flatMap(page => (Array.isArray(page) ? page : (page['hydra:member'] ?? []))),
    [data],
  )

  return {
    data: items,
    mutate,
    size,
    setSize,
    isValidating,
    total:
      firstPage && !Array.isArray(firstPage) ? (firstPage['hydra:totalItems'] ?? firstPage.nbResults ?? null) : null,
    lastPage: lastPage ?? null,
  }
}

export { useSWRConfig }
